import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Upload, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { BrutalTextarea } from '@/components/ui/BrutalTextarea';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { Checkbox } from '@/components/ui/checkbox';
import { jobApplicationSchema, JobApplicationFormData } from '@/lib/jobValidation';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { JobPosting } from '@/data/jobOptions';
import { sanitizeName, sanitizePhone, sanitizeText } from '@/lib/sanitize';

interface JobApplicationModalProps {
  job: JobPosting;
  open: boolean;
  onClose: () => void;
}

export function JobApplicationModal({ job, open, onClose }: JobApplicationModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const form = useForm<JobApplicationFormData>({
    resolver: zodResolver(jobApplicationSchema),
    defaultValues: {
      applicant_name: '',
      applicant_email: user?.email || '',
      applicant_phone: '',
      cover_letter: '',
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: t('jobs.apply.errors.fileTooLarge'),
          variant: 'error',
        });
        return;
      }
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t('jobs.apply.errors.invalidFileType'),
          variant: 'error',
        });
        return;
      }
      setCvFile(file);
    }
  };

  const onSubmit = async (data: JobApplicationFormData) => {
    if (!privacyAccepted) {
      toast({
        title: t('jobs.apply.errors.privacyRequired'),
        variant: 'error',
      });
      return;
    }

    if (job.application_method === 'upload_cv' && !cvFile) {
      toast({
        title: t('jobs.apply.errors.cvRequired'),
        variant: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let cvUrl: string | null = null;

      // Upload CV if provided
      if (cvFile && user) {
        const fileName = `${user.id}/${job.id}/${Date.now()}-${cvFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('job-cv-uploads')
          .upload(fileName, cvFile);

        if (uploadError) throw uploadError;
        cvUrl = uploadData.path;
      }

      // Submit application via edge function with sanitized data
      const { data: result, error } = await supabase.functions.invoke('submit-job-application', {
        body: {
          job_posting_id: job.id,
          applicant_name: sanitizeName(data.applicant_name),
          applicant_email: data.applicant_email,
          applicant_phone: data.applicant_phone ? sanitizePhone(data.applicant_phone) : null,
          cover_letter: data.cover_letter ? sanitizeText(data.cover_letter) : null,
          cv_file_url: cvUrl,
        },
      });

      if (error) throw error;

      toast({
        title: t('jobs.apply.success'),
        description: t('jobs.apply.successDesc'),
      });

      onClose();
      form.reset();
      setCvFile(null);
      setPrivacyAccepted(false);
    } catch (error: any) {
      console.error('Application error:', error);
      toast({
        title: t('jobs.apply.errors.generic'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto border-[3px] border-border shadow-[6px_6px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {t('jobs.apply.title', { position: job.title })}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="applicant_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('jobs.apply.name')} *</FormLabel>
                  <FormControl>
                    <BrutalInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="applicant_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('jobs.apply.email')} *</FormLabel>
                  <FormControl>
                    <BrutalInput type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="applicant_phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('jobs.apply.phone')}</FormLabel>
                  <FormControl>
                    <BrutalInput {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {job.application_method === 'upload_cv' && (
              <div>
                <label className="text-sm font-medium block mb-2">
                  {t('jobs.apply.cv')} *
                </label>
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                  {cvFile ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{cvFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setCvFile(null)}
                        className="text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {t('jobs.apply.cvHint')}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="cover_letter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('jobs.apply.coverLetter')}</FormLabel>
                  <FormControl>
                    <BrutalTextarea 
                      {...field} 
                      value={field.value || ''} 
                      rows={4}
                      maxLength={1000}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t('jobs.apply.coverLetterHint')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-start gap-2">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={(checked) => setPrivacyAccepted(checked === true)}
              />
              <label htmlFor="privacy" className="text-sm cursor-pointer">
                {t('jobs.apply.privacy')}
              </label>
            </div>

            <BrutalButton
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('jobs.apply.submit')
              )}
            </BrutalButton>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
